<template>
  <v-container fluid class="analysis-runs-page">
    <!-- Header -->
    <div class="analysis-runs-page__header">
      <div class="analysis-runs-page__title">
        <v-icon left color="primary">mdi-play-box-multiple-outline</v-icon>
        <span class="headline">Runs</span>
        <v-chip v-if="!loading" small outlined class="ml-3">{{ total }}</v-chip>
        <v-spacer />
        <v-select
          v-if="folders.length"
          v-model="folderFilter"
          :items="folderItems"
          item-text="text"
          item-value="value"
          label="Folder"
          dense
          outlined
          hide-details
          clearable
          class="analysis-runs-page__filter mr-3"
          attach
        />
        <v-btn small outlined class="mr-2" :loading="loading" @click="load">
          <v-icon small left>mdi-refresh</v-icon>
          Refresh
        </v-btn>
        <v-btn
          small
          outlined
          class="mr-2 analysis-runs-page__compare"
          :disabled="runs.length < 2"
          @click="openCompare()"
        >
          <v-icon small left>mdi-compare-horizontal</v-icon>
          Compare runs
        </v-btn>
        <v-btn
          small
          color="primary"
          depressed
          class="analysis-runs-page__launch"
          @click="openLaunch"
        >
          <v-icon small left>mdi-play</v-icon>
          Launch run
        </v-btn>
      </div>
      <div class="caption grey--text text--darken-1 analysis-runs-page__subtitle">
        A run is one folder, one configuration and one engine version measured together at a point
        in time. The three metrics are the Coherence Evaluation metrics of the videos it measured.
        <router-link to="/engine-changelog" class="analysis-runs-page__changelog-link">
          View engine changelog
        </router-link>
      </div>
    </div>

    <!-- Stale engine versions: the reason a delta may not be attributable. -->
    <v-alert
      v-if="outdatedNotice"
      dense
      text
      type="warning"
      class="analysis-runs-page__outdated"
      icon="mdi-alert-outline"
    >
      {{ outdatedNotice }}. Re-run them with the current version before comparing.
    </v-alert>

    <!-- A failed background poll never hides the rows; it says so above them. -->
    <v-alert v-if="refreshError" dense text type="warning" class="analysis-runs-page__outdated">
      {{ refreshError }}
    </v-alert>

    <!-- Error -->
    <v-alert v-if="error" type="error" outlined class="analysis-runs-page__error">
      <div class="d-flex align-center">
        <span>{{ error }}</span>
        <v-spacer />
        <v-btn small outlined color="error" @click="load">
          <v-icon small left>mdi-refresh</v-icon>
          Retry
        </v-btn>
      </div>
    </v-alert>

    <!-- Loading -->
    <v-skeleton-loader
      v-else-if="loading && !runs.length"
      type="list-item-three-line@4"
      class="mt-4"
    />

    <!-- Empty -->
    <v-card v-else-if="!runs.length" outlined class="analysis-runs-page__empty">
      <v-icon size="48" color="grey lighten-1">mdi-play-box-outline</v-icon>
      <div class="subtitle-1 grey--text text--darken-1 mt-2">No runs yet</div>
      <div class="caption grey--text mb-3">
        Launch a run to measure a whole folder with one configuration and the engine version
        deployed right now.
      </div>
      <v-btn small color="primary" depressed @click="openLaunch">
        <v-icon small left>mdi-play</v-icon>
        Launch run
      </v-btn>
    </v-card>

    <!-- Runs, most recent first -->
    <div v-else class="analysis-runs-page__list">
      <v-card
        v-for="row in rows"
        :key="row.id"
        outlined
        class="analysis-runs-page__row"
        :class="{ 'analysis-runs-page__row--active': row.active }"
        @click="openRun(row.id)"
      >
        <div class="analysis-runs-page__row-body">
          <!-- Identity and provenance -->
          <div class="analysis-runs-page__identity">
            <div class="analysis-runs-page__row-title">
              <span class="subtitle-1 font-weight-bold">{{ row.title }}</span>
              <v-chip x-small label :color="row.status.color" dark class="ml-2">
                <v-icon x-small left>{{ row.status.icon }}</v-icon>
                {{ row.status.label }}
              </v-chip>
              <FolderRunFreshnessChip :freshness="row.run.freshness" class="ml-2" />
            </div>

            <div class="caption grey--text text--darken-1 analysis-runs-page__meta">
              {{ row.meta }}
            </div>

            <div class="analysis-runs-page__chips">
              <v-chip
                v-for="chip in row.engineChips"
                :key="chip.key"
                x-small
                outlined
                label
                :color="chip.color || undefined"
                :title="chip.tooltip"
              >
                {{ chip.text }}
              </v-chip>
              <v-chip
                v-if="row.buildChip"
                x-small
                outlined
                label
                class="analysis-runs-page__build"
                :title="row.buildChip.tooltip"
              >
                {{ row.buildChip.text }}
              </v-chip>
              <span v-if="!row.engineChips.length" class="caption grey--text">
                Engine versions not recorded
              </span>
            </div>

            <!-- Live progress while the run is moving -->
            <div v-if="row.active" class="analysis-runs-page__progress">
              <v-progress-linear
                :value="row.progressValue"
                height="6"
                rounded
                color="primary"
                class="analysis-runs-page__progress-bar"
              />
              <span class="caption font-weight-medium">{{ row.progressText }}</span>
              <span class="caption grey--text ml-2">{{ row.time }} elapsed</span>
            </div>
            <div v-else class="caption grey--text analysis-runs-page__timing">
              {{ row.finishedCaption }}
            </div>

            <p v-if="row.run.note" class="caption grey--text text--darken-1 mb-0 mt-1">
              {{ row.run.note }}
            </p>
          </div>

          <!-- Metrics + delta -->
          <div class="analysis-runs-page__metrics">
            <FolderRunMetrics
              dense
              :metrics="row.run.metrics"
              :delta="row.run.delta"
              :against-label="row.againstLabel"
            />
          </div>

          <!-- Actions -->
          <div class="analysis-runs-page__actions" @click.stop>
            <v-btn icon small title="Open run" @click="openRun(row.id)">
              <v-icon small>mdi-open-in-new</v-icon>
            </v-btn>
            <v-menu offset-y>
              <template #activator="{ on, attrs }">
                <v-btn icon small v-bind="attrs" v-on="on">
                  <v-icon small>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item @click="rerun(row.run)">
                  <v-list-item-title>Re-run with current version</v-list-item-title>
                </v-list-item>
                <v-list-item :disabled="runs.length < 2" @click="openCompare(row.id)">
                  <v-list-item-title>Compare with…</v-list-item-title>
                </v-list-item>
                <v-divider />
                <v-list-item @click="confirmDelete(row.run)">
                  <v-list-item-title class="error--text">Delete run</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </div>
      </v-card>
    </div>

    <LaunchFolderRunDialog v-model="launchDialog" @launched="onLaunched" />
    <FolderRunCompareDialog
      v-model="compareDialog.open"
      :runs="runs"
      :initial-a="compareDialog.a"
      :initial-b="compareDialog.b"
    />

    <v-dialog v-model="deleteDialog.open" max-width="440">
      <v-card>
        <v-card-title class="px-6">
          <h5 class="primary-color mb-0">Delete run</h5>
        </v-card-title>
        <v-card-text class="px-6">
          Delete <strong>{{ deleteDialog.title }}</strong
          >? Only the run record is removed — the analyses of its videos and their results are kept.
          <v-alert v-if="deleteDialog.error" dense text type="error" class="mt-3 mb-0">
            {{ deleteDialog.error }}
          </v-alert>
        </v-card-text>
        <v-divider />
        <v-card-actions class="px-6 py-3">
          <v-spacer />
          <v-btn
            text
            class="cancel-modal"
            :disabled="deleteDialog.busy"
            @click="deleteDialog.open = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="error"
            depressed
            :loading="deleteDialog.busy"
            class="analysis-runs-page__confirm-delete"
            @click="remove"
          >
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.visible" :timeout="6000" :color="snackbar.color" bottom>
      {{ snackbar.text }}
      <template #action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.visible = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import FolderRunCompareDialog from '@/components/folderRuns/FolderRunCompareDialog.vue';
import FolderRunFreshnessChip from '@/components/folderRuns/FolderRunFreshnessChip.vue';
import FolderRunMetrics from '@/components/folderRuns/FolderRunMetrics.vue';
import LaunchFolderRunDialog from '@/components/folderRuns/LaunchFolderRunDialog.vue';
import FolderRunsService from '@/services/FolderRunsService';
import ManualAnalysisService from '@/services/ManualAnalysisService';
import { POLL_INTERVAL_MS } from '@/store/modules/analysisQueue';
import {
  configCaption,
  describeLaunchError,
  engineVersionChips,
  folderCaption,
  folderRunStatusPresentation,
  formatDateTime,
  formatDurationMs,
  formatRunTime,
  isFolderRunActive,
  outdatedRunsNotice,
  progressText,
  progressValue,
  shortHash,
} from '@/utils/folderRuns';

/**
 * /runs — the Folder Runs home (behind the `folderRuns` flag).
 *
 * Ordered by date, newest first. Each row answers the same four questions in the same order:
 * what was measured, how it scored, how that compares with the previous comparable run, and
 * whether the run is still trustworthy (freshness).
 *
 * Running rows poll every 10 s; polling pauses while the tab is hidden, exactly like
 * AnalysisQueue, so a forgotten tab does not hammer the API.
 */
export default {
  name: 'AnalysisRunsList',

  components: {
    FolderRunCompareDialog,
    FolderRunFreshnessChip,
    FolderRunMetrics,
    LaunchFolderRunDialog,
  },

  data() {
    return {
      runs: [],
      total: 0,
      folders: [],
      folderFilter: null,
      loading: false,
      error: '',
      refreshError: '',
      launchDialog: false,
      compareDialog: { open: false, a: null, b: null },
      deleteDialog: { open: false, run: null, title: '', busy: false, error: '' },
      snackbar: { visible: false, text: '', color: 'success' },
      pollTimer: null,
      visibilityHandler: null,
    };
  },

  computed: {
    runsById() {
      const map = new Map();
      this.runs.forEach((run) => map.set(Number(run.id), run));
      return map;
    },
    hasActiveRuns() {
      return this.runs.some(isFolderRunActive);
    },
    outdatedNotice() {
      return outdatedRunsNotice(
        this.runs.filter((run) => run?.freshness?.state === 'outdated').length
      );
    },
    folderItems() {
      return this.folders.map((folder) => ({ value: Number(folder.id), text: folder.name }));
    },
    rows() {
      return this.runs.map((run) => {
        const active = isFolderRunActive(run);
        return {
          id: Number(run.id),
          run,
          active,
          title: run.name || folderCaption(run),
          status: folderRunStatusPresentation(run.status),
          meta: this.metaLine(run),
          engineChips: engineVersionChips(run.engine_versions, {
            outdatedEngines: run?.freshness?.outdated_engines,
          }),
          buildChip: run.build_sha
            ? {
                text: `build ${shortHash(run.build_sha, 7)}`,
                tooltip: `Worker build ${run.build_sha}`,
              }
            : null,
          progressText: progressText(run),
          progressValue: progressValue(run),
          time: formatRunTime(run),
          finishedCaption: this.finishedCaption(run),
          againstLabel: this.againstLabel(run),
        };
      });
    },
  },

  watch: {
    folderFilter() {
      this.load();
    },
  },

  mounted() {
    this.load();
    this.startPolling();
  },

  beforeDestroy() {
    this.stopPolling();
  },

  methods: {
    async load({ silent = false } = {}) {
      if (!silent) {
        this.loading = true;
        this.error = '';
      }
      try {
        const response = await FolderRunsService.list({
          limit: 100,
          folder_id: this.folderFilter ?? undefined,
        });
        const body = response?.data?.data ?? response?.data ?? {};
        const items = Array.isArray(body.items) ? body.items : Array.isArray(body) ? body : [];
        this.runs = items;
        this.total = Number.isFinite(Number(body.total)) ? Number(body.total) : items.length;
        this.error = '';
        this.refreshError = '';
      } catch (error) {
        // A failed background refresh must not wipe the rows already on screen, so it is
        // reported as a banner above the list instead of replacing it.
        if (silent) {
          this.refreshError = 'The list could not be refreshed. What you see may be stale.';
        } else {
          this.runs = [];
          this.error = 'Failed to load the runs.';
        }
      } finally {
        this.loading = false;
      }
      if (!this.folders.length) this.loadFolders();
    },

    async loadFolders() {
      try {
        const response = await ManualAnalysisService.getFolders();
        const items = response?.data?.data ?? response?.data;
        this.folders = Array.isArray(items) ? items : [];
      } catch (error) {
        this.folders = [];
      }
    },

    /** Poll only while something is moving, and only while the tab is visible. */
    startPolling() {
      this.stopPolling();
      this.pollTimer = setInterval(() => {
        if (this.hasActiveRuns) this.load({ silent: true });
      }, POLL_INTERVAL_MS);
      this.visibilityHandler = () => {
        if (typeof document === 'undefined') return;
        if (document.visibilityState === 'hidden') {
          this.clearTimer();
        } else if (!this.pollTimer) {
          this.load({ silent: true });
          this.startPolling();
        }
      };
      if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', this.visibilityHandler);
      }
    },

    clearTimer() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },

    stopPolling() {
      this.clearTimer();
      if (this.visibilityHandler && typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', this.visibilityHandler);
      }
      this.visibilityHandler = null;
    },

    /** 'Folder A · 12 videos · config v3 · 2026-09-17 14:02'. */
    metaLine(run) {
      const parts = [];
      if (run.name) parts.push(folderCaption(run));
      parts.push(configCaption(run.config));
      if (Array.isArray(run.engines) && run.engines.length) parts.push(run.engines.join(', '));
      parts.push(formatDateTime(run.created_at));
      if (run.rerun_of != null) parts.push(`re-run of #${run.rerun_of}`);
      return parts.filter(Boolean).join(' · ');
    },

    finishedCaption(run) {
      const counts = run.counts || {};
      const parts = [];
      if (counts.succeeded) parts.push(`${counts.succeeded} succeeded`);
      if (counts.failed) parts.push(`${counts.failed} failed`);
      if (counts.cancelled) parts.push(`${counts.cancelled} cancelled`);
      if (counts.missing) parts.push(`${counts.missing} never queued`);
      const duration = formatDurationMs(run.duration_ms);
      if (duration !== '—') parts.push(`${duration} total`);
      return parts.join(' · ');
    },

    /** 'vs Baseline' — names the run the delta is measured against. */
    againstLabel(run) {
      const againstId = run?.delta?.against_folder_run_id;
      if (againstId == null) return '';
      const other = this.runsById.get(Number(againstId));
      const name = other ? other.name || formatDateTime(other.created_at) : `run #${againstId}`;
      return `vs ${name}`;
    },

    openRun(id) {
      this.$router.push(`/runs/${id}`);
    },

    openLaunch() {
      this.launchDialog = true;
    },

    openCompare(id = null) {
      const ids = this.runs.map((run) => Number(run.id));
      this.compareDialog = {
        open: true,
        a: id != null ? (ids.find((other) => other !== Number(id)) ?? null) : (ids[1] ?? null),
        b: id != null ? Number(id) : (ids[0] ?? null),
      };
    },

    notify(text, color = 'success') {
      this.snackbar = { visible: true, text, color };
    },

    onLaunched(result) {
      const accepted = result.accepted.length;
      const parts = [`Queued ${accepted} ${accepted === 1 ? 'video' : 'videos'}`];
      if (result.rejected.length) parts.push(`${result.rejected.length} skipped`);
      this.notify(parts.join(' · '), accepted ? 'success' : 'warning');
      this.load();
      this.startPolling();
    },

    async rerun(run) {
      try {
        const response = await FolderRunsService.rerun(run.id, { socketId: this.$socketId });
        const body = response?.data?.data ?? response?.data ?? {};
        const accepted = Array.isArray(body.accepted) ? body.accepted.length : 0;
        this.notify(`Re-run queued · ${accepted} ${accepted === 1 ? 'video' : 'videos'}`);
        this.load();
      } catch (error) {
        this.notify(describeLaunchError(error), 'error');
      }
    },

    confirmDelete(run) {
      this.deleteDialog = {
        open: true,
        run,
        title: run.name || folderCaption(run),
        busy: false,
        error: '',
      };
    },

    async remove() {
      const run = this.deleteDialog.run;
      if (!run) return;
      this.deleteDialog.busy = true;
      this.deleteDialog.error = '';
      try {
        await FolderRunsService.remove(run.id);
        this.deleteDialog.open = false;
        this.notify('Run deleted');
        this.load();
      } catch (error) {
        this.deleteDialog.error =
          error?.response?.data?.message || 'Could not delete the run. Try again.';
      } finally {
        this.deleteDialog.busy = false;
      }
    },
  },
};
</script>

<style scoped>
.analysis-runs-page {
  padding-top: 72px;
  max-width: 1400px;
}
.analysis-runs-page__header {
  margin-bottom: 16px;
}
.analysis-runs-page__title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}
.analysis-runs-page__filter {
  max-width: 220px;
}
.analysis-runs-page__subtitle {
  margin-top: 4px;
  max-width: 880px;
}
.analysis-runs-page__changelog-link {
  text-decoration: none;
}
.analysis-runs-page__outdated {
  margin-bottom: 12px;
}
.analysis-runs-page__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 16px;
  text-align: center;
  border-radius: 10px !important;
}
.analysis-runs-page__list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.analysis-runs-page__row {
  border-radius: 10px !important;
  cursor: pointer;
  transition:
    border-color 0.2s ease,
    background-color 0.2s ease;
}
.analysis-runs-page__row:hover {
  border-color: rgba(0, 0, 0, 0.24) !important;
  background-color: rgba(0, 0, 0, 0.015);
}
.analysis-runs-page__row--active {
  border-left: 3px solid #1976d2 !important;
}
.analysis-runs-page__row-body {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 14px 16px;
  flex-wrap: wrap;
}
.analysis-runs-page__identity {
  flex: 1 1 380px;
  min-width: 0;
}
.analysis-runs-page__row-title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  row-gap: 4px;
}
.analysis-runs-page__meta {
  margin-top: 2px;
}
.analysis-runs-page__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
}
.analysis-runs-page__progress {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  flex-wrap: wrap;
}
.analysis-runs-page__progress-bar {
  flex: 1 1 160px;
  max-width: 220px;
}
.analysis-runs-page__timing {
  margin-top: 8px;
  display: block;
  min-height: 16px;
}
.analysis-runs-page__metrics {
  flex: 0 1 380px;
  min-width: 280px;
}
.analysis-runs-page__actions {
  display: flex;
  align-items: center;
  margin-left: auto;
}
</style>
